import React from 'react';
import { Projectss } from '../data/data';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';

const Project = () => {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { type: "spring", stiffness: 70, damping: 15 } 
    }
  };

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Decorative blurred backgrounds */}
      <div className="absolute top-1/3 left-0 w-96 h-96 rounded-full bg-blue-500/5 dark:bg-blue-600/5 blur-3xl" />
      <div className="absolute bottom-1/3 right-0 w-96 h-96 rounded-full bg-purple-500/5 dark:bg-purple-600/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header Text */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('projectLabel', 'Portfolio')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('projectHeading')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
          <p className="mt-4 text-slate-500 dark:text-neutral-400 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
            {t('projectSubHeading')}
          </p>
        </div>

        {/* Projects Layout Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.05 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10"
        >
          {Projectss.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="group relative flex flex-col rounded-3xl bg-slate-50/50 dark:bg-[#111111]/30 border border-slate-200/50 dark:border-neutral-800/40 hover:border-blue-500/25 dark:hover:border-purple-500/25 hover:shadow-2xl hover:shadow-blue-500/5 dark:hover:shadow-purple-500/5 transition-all duration-300 overflow-hidden"
            >
              {/* Image Frame with overflow hidden */}
              <div className="relative w-full aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-neutral-900">
                {/* Image Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none" />
                
                <img
                  src={project.image}
                  alt={t(project.title)}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
              </div>

              {/* Text / Details Container */}
              <div className="p-6 sm:p-8 flex flex-col justify-between flex-grow">
                <div>
                  {/* Technology Tags */}
                  {project.techs && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.techs.map((tech, idx) => (
                        <span 
                          key={idx} 
                          className="px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-600 dark:text-neutral-400 border border-slate-200/50 dark:border-neutral-700/30"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-white mb-3">
                    {t(project.title, project.title.replace(/_/g, ' '))}
                  </h3>

                  {/* Subtitle / Description */}
                  <p className="text-sm text-slate-500 dark:text-neutral-400 leading-relaxed mb-6">
                    {t(project.subtitle, 'Successful implementation of a scalable business application matching enterprise requirements.')}
                  </p>
                </div>

                {/* Case Study Link Button */}
                <div className="flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-purple-400 hover:text-blue-700 dark:hover:text-purple-300 w-fit cursor-pointer group/btn">
                  <span>{t('view_project_details', 'View Project Case Study')}</span>
                  <FiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1.5" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Project;